import { create } from 'zustand';
import { adminAPI } from '../services/adminApi';

const useAdminStore = create((set, get) => ({
  // State
  isAdmin: null, // null = unknown, true = admin, false = not admin
  loading: false,
  error: null,

  // Dashboard data
  dashboard: null,

  // Users
  users: [],
  usersTotal: 0,
  usersPage: 1,

  // Challenges
  challenges: [],

  // Components
  components: [],

  // Settings
  settings: [],

  // Whitelist
  whitelist: [],

  // Activity Log
  activityLog: [],
  activityTotal: 0,

  // Synergies
  synergies: [],
  componentSynergies: [],

  // Analytics
  userAnalytics: null,
  challengeAnalytics: null,
  activityAnalytics: null,

  // Actions
  checkAdminAccess: async () => {
    try {
      set({ loading: true, error: null });
      const response = await adminAPI.getDashboard();
      set({ isAdmin: true, dashboard: response.data, loading: false });
      return true;
    } catch (error) {
      set({ isAdmin: false, loading: false });
      return false;
    }
  },

  fetchDashboard: async () => {
    try {
      set({ loading: true, error: null });
      const response = await adminAPI.getDashboard();
      set({ dashboard: response.data, loading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch dashboard', loading: false });
      throw error;
    }
  },

  // User Management
  fetchUsers: async (params = {}) => {
    try {
      set({ loading: true, error: null });
      const response = await adminAPI.listUsers(params);
      set({
        users: response.data.data || [],
        usersTotal: response.data.total || 0,
        usersPage: response.data.page || 1,
        loading: false,
      });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch users', loading: false });
      throw error;
    }
  },

  updateUser: async (id, data) => {
    try {
      await adminAPI.updateUser(id, data);
      // Refresh users list
      const { usersPage } = get();
      await get().fetchUsers({ page: usersPage });
    } catch (error) {
      throw error;
    }
  },

  banUser: async (id, reason) => {
    try {
      await adminAPI.banUser(id, reason);
      const { usersPage } = get();
      await get().fetchUsers({ page: usersPage });
    } catch (error) {
      throw error;
    }
  },

  unbanUser: async (id) => {
    try {
      await adminAPI.unbanUser(id);
      const { usersPage } = get();
      await get().fetchUsers({ page: usersPage });
    } catch (error) {
      throw error;
    }
  },

  updateUserDP: async (id, designPoints) => {
    try {
      await adminAPI.updateUserDP(id, designPoints);
      const { usersPage } = get();
      await get().fetchUsers({ page: usersPage });
    } catch (error) {
      throw error;
    }
  },

  deleteUser: async (id) => {
    try {
      await adminAPI.deleteUser(id);
      const { usersPage } = get();
      await get().fetchUsers({ page: usersPage });
    } catch (error) {
      throw error;
    }
  },

  togglePremium: async (id, isPremium, duration = '1year') => {
    try {
      await adminAPI.togglePremium(id, isPremium, duration);
      const { usersPage } = get();
      await get().fetchUsers({ page: usersPage });
    } catch (error) {
      throw error;
    }
  },

  getUserSubmissions: async (id, params = {}) => {
    try {
      const response = await adminAPI.getUserSubmissions(id, params);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  resetUserProgress: async (id) => {
    try {
      await adminAPI.resetUserProgress(id);
      const { usersPage } = get();
      await get().fetchUsers({ page: usersPage });
    } catch (error) {
      throw error;
    }
  },

  // Challenge Management
  fetchChallenges: async () => {
    try {
      set({ loading: true, error: null });
      const response = await adminAPI.listChallenges();
      set({ challenges: response.data.challenges || [], loading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch challenges', loading: false });
      throw error;
    }
  },

  createChallenge: async (data) => {
    try {
      const response = await adminAPI.createChallenge(data);
      await get().fetchChallenges();
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateChallenge: async (id, data) => {
    try {
      await adminAPI.updateChallenge(id, data);
      await get().fetchChallenges();
    } catch (error) {
      throw error;
    }
  },

  deleteChallenge: async (id) => {
    try {
      await adminAPI.deleteChallenge(id);
      await get().fetchChallenges();
    } catch (error) {
      throw error;
    }
  },

  // Component Management
  fetchComponents: async (params = {}) => {
    try {
      set({ loading: true, error: null });
      const response = await adminAPI.listComponents(params);
      set({ components: response.data.components || [], loading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch components', loading: false });
      throw error;
    }
  },

  createComponent: async (data) => {
    try {
      const response = await adminAPI.createComponent(data);
      await get().fetchComponents();
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateComponent: async (id, data) => {
    try {
      await adminAPI.updateComponent(id, data);
      await get().fetchComponents();
    } catch (error) {
      throw error;
    }
  },

  deleteComponent: async (id) => {
    try {
      await adminAPI.deleteComponent(id);
      await get().fetchComponents();
    } catch (error) {
      throw error;
    }
  },

  // Settings
  fetchSettings: async () => {
    try {
      set({ loading: true, error: null });
      const response = await adminAPI.getSettings();
      set({ settings: response.data.settings || [], loading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch settings', loading: false });
      throw error;
    }
  },

  updateSetting: async (key, value) => {
    try {
      await adminAPI.updateSetting(key, value);
      await get().fetchSettings();
    } catch (error) {
      throw error;
    }
  },

  // Whitelist
  fetchWhitelist: async () => {
    try {
      set({ loading: true, error: null });
      const response = await adminAPI.getWhitelist();
      set({ whitelist: response.data.whitelist || [], loading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch whitelist', loading: false });
      throw error;
    }
  },

  addToWhitelist: async (email, notes) => {
    try {
      await adminAPI.addToWhitelist(email, notes);
      await get().fetchWhitelist();
    } catch (error) {
      throw error;
    }
  },

  removeFromWhitelist: async (id) => {
    try {
      await adminAPI.removeFromWhitelist(id);
      await get().fetchWhitelist();
    } catch (error) {
      throw error;
    }
  },

  // Activity Log
  fetchActivityLog: async (params = {}) => {
    try {
      set({ loading: true, error: null });
      const response = await adminAPI.getActivityLog(params);
      set({
        activityLog: response.data.data || [],
        activityTotal: response.data.total || 0,
        loading: false,
      });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch activity log', loading: false });
      throw error;
    }
  },

  // Analytics
  fetchUserAnalytics: async (period = '30d') => {
    try {
      const response = await adminAPI.getUserAnalytics(period);
      set({ userAnalytics: response.data });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  fetchChallengeAnalytics: async () => {
    try {
      const response = await adminAPI.getChallengeAnalytics();
      set({ challengeAnalytics: response.data });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  fetchActivityAnalytics: async (period = '30d') => {
    try {
      const response = await adminAPI.getActivityAnalytics(period);
      set({ activityAnalytics: response.data });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Synergy Management
  fetchSynergies: async (params = {}) => {
    try {
      set({ loading: true, error: null });
      const response = await adminAPI.listSynergies(params);
      set({ synergies: response.data.synergies || [], loading: false });
      return response.data;
    } catch (error) {
      set({ error: error.response?.data?.error || 'Failed to fetch synergies', loading: false });
      throw error;
    }
  },

  fetchComponentSynergies: async (componentId) => {
    try {
      const response = await adminAPI.getComponentSynergies(componentId);
      set({ componentSynergies: response.data.synergies || [] });
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  createSynergy: async (data) => {
    try {
      const response = await adminAPI.createSynergy(data);
      await get().fetchSynergies();
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  updateSynergy: async (id, data) => {
    try {
      await adminAPI.updateSynergy(id, data);
      await get().fetchSynergies();
    } catch (error) {
      throw error;
    }
  },

  deleteSynergy: async (id) => {
    try {
      await adminAPI.deleteSynergy(id);
      await get().fetchSynergies();
    } catch (error) {
      throw error;
    }
  },

  // Reset state
  reset: () => {
    set({
      isAdmin: null,
      loading: false,
      error: null,
      dashboard: null,
      users: [],
      usersTotal: 0,
      usersPage: 1,
      challenges: [],
      components: [],
      settings: [],
      whitelist: [],
      activityLog: [],
      activityTotal: 0,
      synergies: [],
      componentSynergies: [],
      userAnalytics: null,
      challengeAnalytics: null,
      activityAnalytics: null,
    });
  },
}));

export default useAdminStore;
